import React, { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { Button } from '../atoms';
import { Checkbox } from '../atoms';
import type {
  SubscriptionPlan,
  SubscriptionPlanCreateRequest,
  SubscriptionPlanUpdateRequest,
  UserTier,
} from '../../types/api/subscription';
import {
  USER_TIERS,
  MIN_PLAN_DURATION_MONTHS,
  MAX_PLAN_DURATION_MONTHS,
} from '../../types/api/subscription';

const DEFAULT_DURATION_MONTHS = 3;

export interface SubscriptionPlanFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: SubscriptionPlanCreateRequest | SubscriptionPlanUpdateRequest) => void | Promise<void>;
  mode: 'create' | 'edit';
  plan?: SubscriptionPlan;
}

type PlanFormState = {
  name: string;
  description: string;
  // Duration in months. Captured as a raw string so users can type freely (e.g. "1", "12", "24"),
  // including a temporarily empty value. Validated and parsed to a number on submit.
  durationMonths: string;
  price: number;
  currency: string;
  discountPercentage: number;
  originalPrice: number;
  tierGranted: UserTier;
  // Features
  downloadEnabled: boolean;
  maxDownloads: number | null;
  hdEnabled: boolean;
  uhdEnabled: boolean;
  adsEnabled: boolean;
  maxDevices: number | null;
  offlineViewing: boolean;
  // Display
  isFeatured: boolean;
  sortOrder: number;
};

const createDefaultState = (): PlanFormState => ({
  name: '',
  description: '',
  durationMonths: String(DEFAULT_DURATION_MONTHS),
  price: 0,
  currency: 'MMK',
  discountPercentage: 0,
  originalPrice: 0,
  tierGranted: 'PREMIUM',
  downloadEnabled: true,
  maxDownloads: null,
  hdEnabled: true,
  uhdEnabled: false,
  adsEnabled: false,
  maxDevices: null,
  offlineViewing: true,
  isFeatured: false,
  sortOrder: 0,
});

export const SubscriptionPlanFormModal: React.FC<SubscriptionPlanFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode,
  plan,
}) => {
  const [formData, setFormData] = useState<PlanFormState>(createDefaultState());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Pre-fill form when editing
  useEffect(() => {
    if (plan && mode === 'edit' && isOpen) {
      setFormData({
        name: plan.name || '',
        description: plan.description || '',
        durationMonths: String(plan.durationMonths ?? DEFAULT_DURATION_MONTHS),
        price: plan.price || 0,
        currency: plan.currency || 'MMK',
        discountPercentage: plan.discountPercentage || 0,
        originalPrice: plan.originalPrice || 0,
        tierGranted: plan.tierGranted || 'PREMIUM',
        downloadEnabled: plan.features?.downloadEnabled ?? true,
        maxDownloads: plan.features?.maxDownloads ?? null,
        hdEnabled: plan.features?.hdEnabled ?? true,
        uhdEnabled: plan.features?.uhdEnabled ?? false,
        adsEnabled: plan.features?.adsEnabled ?? false,
        maxDevices: plan.features?.maxDevices ?? null,
        offlineViewing: plan.features?.offlineViewing ?? true,
        isFeatured: plan.isFeatured ?? false,
        sortOrder: plan.sortOrder || 0,
      });
    } else if (mode === 'create') {
      setFormData(createDefaultState());
    }
    setErrors({});
  }, [plan, mode, isOpen]);

  const handleChange = (field: keyof PlanFormState, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Plan name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Plan name must be less than 100 characters';
    }

    if (formData.description && formData.description.length > 5000) {
      newErrors.description = 'Description must be less than 5000 characters';
    }

    const rawDuration = formData.durationMonths.trim();
    if (!rawDuration) {
      newErrors.durationMonths = 'Duration is required';
    } else {
      const parsed = Number(rawDuration);
      if (
        !Number.isInteger(parsed) ||
        parsed < MIN_PLAN_DURATION_MONTHS ||
        parsed > MAX_PLAN_DURATION_MONTHS
      ) {
        newErrors.durationMonths = `Duration must be a whole number between ${MIN_PLAN_DURATION_MONTHS} and ${MAX_PLAN_DURATION_MONTHS} months`;
      }
    }

    if (formData.price <= 0) {
      newErrors.price = 'Price must be greater than 0';
    }

    if (formData.discountPercentage < 0 || formData.discountPercentage > 100) {
      newErrors.discountPercentage = 'Discount must be between 0 and 100';
    }

    if (formData.originalPrice && formData.originalPrice <= 0) {
      newErrors.originalPrice = 'Original price must be greater than 0';
    }

    if (formData.maxDownloads !== null && formData.maxDownloads < 0) {
      newErrors.maxDownloads = 'Max downloads must be a positive number or leave empty for unlimited';
    }

    if (formData.maxDevices !== null && formData.maxDevices < 0) {
      newErrors.maxDevices = 'Max devices must be a positive number or leave empty for unlimited';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload: SubscriptionPlanCreateRequest | SubscriptionPlanUpdateRequest = {
        name: formData.name,
        description: formData.description || undefined,
        durationMonths: Number(formData.durationMonths),
        price: formData.price,
        currency: formData.currency,
        discountPercentage: formData.discountPercentage || undefined,
        originalPrice: formData.originalPrice || undefined,
        tierGranted: formData.tierGranted,
        downloadEnabled: formData.downloadEnabled,
        maxDownloads: formData.maxDownloads,
        hdEnabled: formData.hdEnabled,
        uhdEnabled: formData.uhdEnabled,
        adsEnabled: formData.adsEnabled,
        maxDevices: formData.maxDevices,
        offlineViewing: formData.offlineViewing,
        isFeatured: formData.isFeatured,
        sortOrder: formData.sortOrder,
      };

      await onSubmit(payload);
      handleClose();
    } catch (error) {
      console.error('Subscription plan form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData(createDefaultState());
      setErrors({});
      onClose();
    }
  };

  const finalPrice = formData.price * (1 - formData.discountPercentage / 100);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={mode === 'create' ? 'Create Subscription Plan' : 'Edit Subscription Plan'} size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Basic Information</h3>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Plan Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
              placeholder="e.g., Premium Annual Plan"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
              placeholder="Describe the subscription plan benefits..."
            />
            {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
          </div>

          {/* Duration and Tier */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration (months) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={String(MAX_PLAN_DURATION_MONTHS).length}
                value={formData.durationMonths}
                onChange={(e) => {
                  // Allow only digits and cap to MAX_PLAN_DURATION_MONTHS digits.
                  const digitsOnly = e.target.value.replace(/[^0-9]/g, '');
                  const trimmed = digitsOnly.slice(0, String(MAX_PLAN_DURATION_MONTHS).length);
                  handleChange('durationMonths', trimmed);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder={`e.g., 3, 6, 12 (between ${MIN_PLAN_DURATION_MONTHS}-${MAX_PLAN_DURATION_MONTHS})`}
                disabled={mode === 'edit'} // Duration cannot be changed after creation
              />
              {errors.durationMonths && (
                <p className="text-red-500 text-xs mt-1">{errors.durationMonths}</p>
              )}
              {mode === 'edit' ? (
                <p className="text-xs text-gray-500 mt-1">Duration cannot be changed</p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  Enter any whole number from {MIN_PLAN_DURATION_MONTHS} to {MAX_PLAN_DURATION_MONTHS} months
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tier Granted <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.tierGranted}
                onChange={(e) => handleChange('tierGranted', e.target.value as UserTier)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
                disabled={mode === 'edit'} // Tier cannot be changed after creation
              >
                {USER_TIERS.filter((t) => t.value !== '').map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              {mode === 'edit' && <p className="text-xs text-gray-500 mt-1">Tier cannot be changed</p>}
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Pricing</h3>

          <div className="grid grid-cols-2 gap-4">
            {/* Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => handleChange('price', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
                min="0"
                step="100"
              />
              {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price}</p>}
            </div>

            {/* Currency */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <input
                type="text"
                value={formData.currency}
                onChange={(e) => handleChange('currency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
                placeholder="MMK"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Original Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Original Price (optional)</label>
              <input
                type="number"
                value={formData.originalPrice || ''}
                onChange={(e) => handleChange('originalPrice', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
                min="0"
                step="100"
                placeholder="Leave empty if no discount"
              />
              {errors.originalPrice && <p className="text-red-500 text-xs mt-1">{errors.originalPrice}</p>}
            </div>

            {/* Discount Percentage */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Discount % (optional)</label>
              <input
                type="number"
                value={formData.discountPercentage || ''}
                onChange={(e) => handleChange('discountPercentage', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
                min="0"
                max="100"
                step="1"
                placeholder="0-100"
              />
              {errors.discountPercentage && <p className="text-red-500 text-xs mt-1">{errors.discountPercentage}</p>}
            </div>
          </div>

          {/* Final Price Display */}
          {(formData.discountPercentage > 0 || formData.originalPrice > 0) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <span className="font-semibold">Final Price:</span> {finalPrice.toLocaleString()} {formData.currency}
                {formData.originalPrice > 0 && (
                  <span className="ml-2 text-gray-600 line-through">{formData.originalPrice.toLocaleString()} {formData.currency}</span>
                )}
              </p>
            </div>
          )}
        </div>

        {/* Features */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Features</h3>

          {/* Feature Checkboxes */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center">
              <Checkbox checked={formData.downloadEnabled} onChange={(e) => handleChange('downloadEnabled', e.target.checked)} />
              <label className="ml-2 text-sm text-gray-700">Enable Downloads</label>
            </div>

            <div className="flex items-center">
              <Checkbox checked={formData.hdEnabled} onChange={(e) => handleChange('hdEnabled', e.target.checked)} />
              <label className="ml-2 text-sm text-gray-700">Enable HD Quality</label>
            </div>

            <div className="flex items-center">
              <Checkbox checked={formData.uhdEnabled} onChange={(e) => handleChange('uhdEnabled', e.target.checked)} />
              <label className="ml-2 text-sm text-gray-700">Enable UHD/4K Quality</label>
            </div>

            <div className="flex items-center">
              <Checkbox checked={formData.offlineViewing} onChange={(e) => handleChange('offlineViewing', e.target.checked)} />
              <label className="ml-2 text-sm text-gray-700">Enable Offline Viewing</label>
            </div>

            <div className="flex items-center">
              <Checkbox checked={formData.adsEnabled} onChange={(e) => handleChange('adsEnabled', e.target.checked)} />
              <label className="ml-2 text-sm text-gray-700">Show Ads</label>
            </div>
          </div>

          {/* Max Downloads */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Downloads</label>
              <input
                type="number"
                value={formData.maxDownloads ?? ''}
                onChange={(e) => handleChange('maxDownloads', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
                min="0"
                placeholder="Leave empty for unlimited"
                disabled={!formData.downloadEnabled}
              />
              {errors.maxDownloads && <p className="text-red-500 text-xs mt-1">{errors.maxDownloads}</p>}
            </div>

            {/* Max Devices */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Devices</label>
              <input
                type="number"
                value={formData.maxDevices ?? ''}
                onChange={(e) => handleChange('maxDevices', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
                min="0"
                placeholder="Leave empty for unlimited"
              />
              {errors.maxDevices && <p className="text-red-500 text-xs mt-1">{errors.maxDevices}</p>}
            </div>
          </div>
        </div>

        {/* Display Options */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Display Options</h3>

          <div className="grid grid-cols-2 gap-4">
            {/* Sort Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
              <input
                type="number"
                value={formData.sortOrder}
                onChange={(e) => handleChange('sortOrder', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
                min="0"
              />
            </div>

            {/* Featured Checkbox */}
            <div className="flex items-center pt-7">
              <Checkbox checked={formData.isFeatured} onChange={(e) => handleChange('isFeatured', e.target.checked)} />
              <label className="ml-2 text-sm text-gray-700">Mark as Featured Plan</label>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" onClick={handleClose} disabled={isSubmitting} variant="secondary">
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : mode === 'create' ? 'Create Plan' : 'Update Plan'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

